import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Copy, ExternalLink, Send, UserPlus, Linkedin, Globe, TrendingUp, CalendarDays, MessageCircle, Star, Clock, StickyNote, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { fmtDatePT, fmtTimePT } from "../lib/time";

export default function AttendeeDrawer({ email, onClose, onBroadcast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [upcoming, setUpcoming] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEvent, setInviteEvent] = useState("");

  useEffect(() => {
    if (!email) return;
    setLoading(true);
    api.get(`/community/attendee/${encodeURIComponent(email)}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load attendee"))
      .finally(() => setLoading(false));
    api.get("/community/upcoming-events").then((r) => setUpcoming(r.data)).catch(() => {});
  }, [email]);

  const reload = () => api.get(`/community/attendee/${encodeURIComponent(email)}`).then((r) => setData(r.data));

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/community/attendee/${encodeURIComponent(email)}/notes`, { body: note.trim() });
      setNote("");
      reload();
      toast.success("Note added");
    } catch { toast.error("Failed"); }
  };

  const deleteNote = async (id) => {
    try { await api.delete(`/community/attendee/${encodeURIComponent(email)}/notes/${id}`); reload(); }
    catch { toast.error("Failed"); }
  };

  const sendInvite = async () => {
    if (!inviteEvent) return toast.error("Pick an event");
    try {
      await api.post(`/community/attendee/${encodeURIComponent(email)}/invite`, { event_id: inviteEvent });
      toast.success("Invitation sent");
      setShowInvite(false); setInviteEvent("");
    } catch { toast.error("Failed to send"); }
  };

  const copyEmail = () => { navigator.clipboard.writeText(email); toast.success("Email copied"); };

  if (!email) return null;

  const p = data?.profile;
  const eng = data?.engagement;
  const warm = data && !data.is_member && (eng?.event_count || 0) >= 2;
  const attendanceRate = eng?.registered ? Math.round((eng.attended / eng.registered) * 100) : 0;

  return (
    <>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(20,22,28,0.28)" }} onClick={onClose} data-testid="attendee-drawer-scrim" />
      <aside className="fixed top-0 right-0 z-50 h-screen w-full sm:w-[520px] flex flex-col" data-testid="attendee-drawer">
        <div className="glass-panel h-full flex flex-col rounded-none sm:rounded-l-3xl overflow-hidden" style={{ background: "hsl(var(--surface) / 0.95)" }}>
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-white/50 shrink-0 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-12 w-12 rounded-2xl flex items-center justify-center font-mono font-semibold text-white shrink-0"
                   style={{ background: data?.is_member ? "hsl(var(--vd-green))" : "hsl(var(--vd-peach-ink))" }}>
                {(p?.name || email).split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-heading text-xl leading-tight truncate">{p?.name || email}</div>
                <div className="text-xs text-muted-foreground font-mono truncate">{email}</div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {data?.is_member
                    ? <span className="chip chip-green text-[10px]">Member</span>
                    : <span className="chip chip-cream text-[10px]">Non-member</span>}
                  {data?.converted && <span className="chip chip-purple text-[10px]">Converted {data.conversion_at ? `· ${fmtDatePT(data.conversion_at, { month: "short", day: "numeric" })}` : ""}</span>}
                  {warm && <span className="chip chip-cream-2 text-[10px]">Warm lead</span>}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-white/60 flex items-center justify-center shrink-0" data-testid="drawer-close"><X size={16} /></button>
          </div>

          {/* Body scroll */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {loading && <div className="skeleton h-40" />}
            {!loading && data && (
              <>
                {/* Actions row */}
                <div className="flex flex-wrap gap-1.5" data-testid="drawer-actions">
                  <button onClick={copyEmail} className="btn-ghost text-xs" data-testid="action-copy-email"><Copy size={11} />Copy email</button>
                  {p?.linkedin && <a href={p.linkedin.startsWith("http") ? p.linkedin : `https://${p.linkedin}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid="action-linkedin"><Linkedin size={11} />LinkedIn</a>}
                  {p?.website && <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid="action-website"><Globe size={11} />Website</a>}
                  <button onClick={() => setShowInvite((v) => !v)} className="btn-ghost text-xs" data-testid="action-invite"><UserPlus size={11} />Invite to event</button>
                  {onBroadcast && <button onClick={() => onBroadcast(email)} className="btn-primary text-xs" data-testid="action-broadcast"><Send size={11} />Broadcast</button>}
                </div>

                {/* Invite pop */}
                {showInvite && (
                  <div className="glass-panel p-4" data-testid="invite-panel">
                    <div className="overline mb-2">Invite to an upcoming event</div>
                    <select className="input-field text-sm" value={inviteEvent} onChange={(e) => setInviteEvent(e.target.value)} data-testid="invite-event-select">
                      <option value="">Pick an event…</option>
                      {upcoming.map((e) => <option key={e.id} value={e.id}>{e.title} · {fmtDatePT(e.start_at, { month: "short", day: "numeric" })}</option>)}
                    </select>
                    <div className="mt-3 flex justify-end gap-2">
                      <button onClick={() => setShowInvite(false)} className="btn-ghost text-xs">Cancel</button>
                      <button onClick={sendInvite} className="btn-primary text-xs" data-testid="invite-send"><Send size={11} />Send invite</button>
                    </div>
                  </div>
                )}

                {/* Conversion context prompt (non-members only) */}
                {!data.is_member && eng?.event_count > 0 && (
                  <div className="rounded-2xl p-4 flex items-start gap-3" style={{ background: "hsl(var(--vd-peach) / 0.14)", border: "1px solid hsl(var(--vd-peach) / 0.35)" }} data-testid="conversion-prompt">
                    <Sparkles size={16} style={{ color: "hsl(var(--vd-peach-ink))" }} className="mt-0.5" />
                    <div className="text-xs" style={{ color: "hsl(var(--vd-peach-ink))" }}>
                      <div className="font-medium">Conversion opportunity</div>
                      <div className="mt-0.5 text-muted-foreground">
                        Attended <span className="font-mono font-semibold" style={{ color: "hsl(var(--vd-peach-ink))" }}>{eng.attended}</span> of {eng.registered} events across {daysBetween(eng.first_event_at, eng.last_event_at)} days. Not yet a member.
                      </div>
                    </div>
                  </div>
                )}

                {/* Profile fields */}
                {(p?.company || p?.working_on) && (
                  <div className="glass-panel p-4" data-testid="profile-block">
                    <div className="overline mb-3">Profile</div>
                    <div className="space-y-2 text-sm">
                      {p.company && <Row label="Company" value={p.company} />}
                      {p.working_on && <div><div className="overline mb-1">What they&apos;re working on</div><div className="text-sm leading-relaxed">{p.working_on}</div></div>}
                    </div>
                  </div>
                )}

                {/* Engagement */}
                <div className="glass-panel p-4" data-testid="engagement-block">
                  <div className="overline mb-3">Engagement</div>
                  <div className="grid grid-cols-2 gap-3">
                    <Stat icon={CalendarDays} label="Attended / registered" value={`${eng.attended} / ${eng.registered}`} sub={`${attendanceRate}%`} />
                    <Stat icon={TrendingUp} label="Events" value={eng.event_count} sub={eng.no_show > 0 ? `${eng.no_show} no-shows` : "on track"} />
                    <Stat icon={Star} label="Feedback" value={eng.avg_rating ?? "—"} sub={`${eng.feedback_count} ratings`} />
                    <Stat icon={Clock} label="First seen" value={fmtDatePT(eng.first_event_at, { month: "short", day: "numeric" })} sub={`Last ${fmtDatePT(eng.last_event_at, { month: "short", day: "numeric" })}`} />
                  </div>
                </div>

                {/* Timeline */}
                <div className="glass-panel p-4" data-testid="timeline-block">
                  <div className="overline mb-3">Timeline</div>
                  <div className="relative pl-4">
                    <div className="absolute left-[7px] top-1 bottom-1 w-px bg-border" />
                    {data.timeline.map((t, i) => <TimelineItem key={`${t.type || "ev"}-${t.at || t.date || i}-${t.event_id || t.title || ""}`} t={t} />)}
                    {data.timeline.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
                  </div>
                </div>

                {/* Notes */}
                <div className="glass-panel p-4" data-testid="notes-block">
                  <div className="flex items-center gap-2 mb-3">
                    <StickyNote size={12} style={{ color: "hsl(var(--vd-green))" }} />
                    <span className="overline">Internal notes</span>
                    <span className="text-[10px] text-muted-foreground">Admin-only</span>
                  </div>
                  <div className="space-y-2">
                    {data.notes.map((n) => (
                      <div key={n.id} className="p-3 rounded-xl border border-white/60 bg-white/40 text-sm relative group" data-testid={`note-${n.id}`}>
                        <div>{n.body}</div>
                        <div className="mt-1 text-[10px] font-mono text-muted-foreground">
                          {n.author_name} · {fmtDatePT(n.created_at, { month: "short", day: "numeric" })} {fmtTimePT(n.created_at)}
                        </div>
                        <button onClick={() => deleteNote(n.id)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" data-testid={`delete-note-${n.id}`}><X size={11} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <input className="input-field text-sm" placeholder="Add a private note…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="note-input" />
                    <button onClick={addNote} className="btn-primary text-sm" data-testid="note-add">Add</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-right truncate">{value}</span>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Icon size={10} />{label}</div>
      <div className="stat-value text-2xl mt-1">{value}</div>
      {sub && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function TimelineItem({ t }) {
  const dotColor = t.type === "capital_series" ? "hsl(var(--vd-purple-ink))"
    : t.type === "feedback" ? "hsl(var(--vd-peach-ink))" : "hsl(var(--vd-green))";
  return (
    <div className="relative pb-4 pl-4">
      <div className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white" style={{ background: dotColor }} />
      {t.type === "registration" && (
        <>
          <div className="text-sm font-medium">{t.event_title}</div>
          <div className="text-[11px] text-muted-foreground font-mono uppercase mt-0.5">
            {fmtDatePT(t.event_start_at, { month: "short", day: "numeric" })} · {t.campus}
            {t.visibility === "members_only" && <span className="ml-1.5 chip chip-purple text-[9px]">Members</span>}
          </div>
          <div className="text-[11px] mt-1">
            {t.attended ? <span className="chip chip-green text-[10px]">Attended</span>
              : t.status === "cancelled" ? <span className="chip chip-coral text-[10px]">Cancelled</span>
              : <span className="chip chip-cream text-[10px]">Registered</span>}
          </div>
        </>
      )}
      {t.type === "capital_series" && (
        <>
          <div className="text-sm font-medium flex items-center gap-1.5"><MessageCircle size={11} />Founder Labs · {t.investor}</div>
          <div className="text-[11px] text-muted-foreground font-mono uppercase mt-0.5">
            {fmtDatePT(t.at, { month: "short", day: "numeric" })} {fmtTimePT(t.at)} PT
          </div>
          <div className="mt-1"><span className={`chip text-[10px] ${t.status === "booked" ? "chip-green" : t.status === "pending" ? "chip-cream-2" : "chip-cream"}`}>{t.status}</span></div>
          {t.pitch_note && <div className="text-[11px] italic text-muted-foreground mt-1">&ldquo;{t.pitch_note}&rdquo;</div>}
        </>
      )}
      {t.type === "feedback" && (
        <>
          <div className="text-sm font-medium">Left feedback · {t.rating}/5</div>
          {t.comment && <div className="text-[11px] italic text-muted-foreground mt-1">&ldquo;{t.comment}&rdquo;</div>}
        </>
      )}
    </div>
  );
}

function daysBetween(a, b) {
  if (!a || !b) return 0;
  return Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000));
}
